import { graphql } from '@apollo/client/react/hoc';
import React, { useState } from 'react';
import { compose } from 'recompose';
import LinkButton from '@/se/components/LinkButton';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import {
  discharge,
  item,
  preDischarge,
  dischargePhysicianLastPatient,
  dischargeAnesthesiologistLastPatient,
} from '../../../../graph/patients';
import { getNotificationTemplate } from '../../../../graph/notificationTemplates';

import { useApolloClient, useQuery } from '@apollo/client';
import { withScope } from '@/contexts/ScopeContext';
import { useRetainedBeaconDischarge } from '@/hooks/useRetainedBeaconDischarge';
import {
  RETAINED_BEACON_DISCHARGE_COPY,
  RetainedBeaconDischargeActions,
  RetainedBeaconDischargeChoice,
} from './RetainedBeaconDischargeChoice';

const BaseDischargeLinkButton = ({ id, patient, mutate, scope }) => {
  const [openLastPatientDialog, setOpenLastPatientDialog] = useState(false);
  const [openAnesthesiologistLastPatientDialog, setOpenAnesthesiologistLastPatientDialog] = useState(false);
  const [openStandardDialog, setOpenStandardDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dischargeError, setDischargeError] = useState(null);
  const client = useApolloClient();
  const retainedBeacon = useRetainedBeaconDischarge({
    patientId: id,
    enabled: scope?.hospital?.modules?.retainedBeaconInpatient?.enabled === true,
    surface: 'liaison',
  });

  const { data: physicianLastProcedure } = useQuery(getNotificationTemplate, {
    variables: { trigger: 'PhysicianLastProcedure', channel: 'SMS', destination: 'Physician' },
  });
  const physicianLastProcedureManual =
    physicianLastProcedure?.getNotificationTemplate?.[0]?.sendingSetting === 'MANUAL';

  const { data: anesthesiologistLastProcedure } = useQuery(getNotificationTemplate, {
    variables: { trigger: 'AnesthesiologistLastProcedure', channel: 'SMS', destination: 'Anesthesiologist' },
  });
  const anesthesiologistLastProcedureManual =
    anesthesiologistLastProcedure?.getNotificationTemplate?.[0]?.sendingSetting === 'MANUAL';

  const executeDischarge = async (
    usePhysicianLastPatient = false,
    useAnesthesiologistLastPatient = false,
    selectedChoice = retainedBeacon.choice
  ) => {
    if (selectedChoice == null && !retainedBeacon.canSubmit) return;

    const keepBeacon = retainedBeacon.prepareSubmission(selectedChoice);
    let closeAfterAttempt = true;
    setIsProcessing(true);
    try {
      await mutate({
        variables: { id },
        mutation: preDischarge,
      });

      await new Promise(resolve => setTimeout(resolve, 100));

      let mutationToUse = discharge;
      if (usePhysicianLastPatient) {
        mutationToUse = dischargePhysicianLastPatient;
      } else if (useAnesthesiologistLastPatient) {
        mutationToUse = dischargeAnesthesiologistLastPatient;
      }

      await mutate({
        variables: { id, keepBeacon },
        mutation: mutationToUse,
        refetchQueries: [
          {
            query: item,
            variables: { id: parseInt(id, 10) },
          },
        ],
      });
    } catch (error) {
      console.error('Discharge process failed', error.message);
      if (error?.graphQLErrors?.some(graphQLError => graphQLError.message === 'RetainedDischargeConflict')) {
        retainedBeacon.setChoice(null);
        closeAfterAttempt = false;
        setDischargeError(RETAINED_BEACON_DISCHARGE_COPY.conflictError);
      } else {
        closeAfterAttempt = false;
        setDischargeError('Discharge failed. Please try again.');
      }
    } finally {
      setIsProcessing(false);
      if (closeAfterAttempt) closeDialogs();
    }
  };

  const initiateDischargeProcess = async () => {
    setDischargeError(null);
    retainedBeacon.begin();
    setIsProcessing(true);
    try {
      await mutate({
        variables: { id },
        mutation: preDischarge,
      });

      await new Promise(resolve => setTimeout(resolve, 100));

      const {
        data: { patient: updatedPatient },
      } = await client.query({
        query: item,
        variables: { id: parseInt(id, 10) },
        fetchPolicy: 'network-only',
      });

      if (updatedPatient?.physicianLastPatient && physicianLastProcedureManual) {
        setOpenLastPatientDialog(true);
      } else if (updatedPatient?.anesthesiologistLastPatient && anesthesiologistLastProcedureManual) {
        setOpenAnesthesiologistLastPatientDialog(true);
      } else {
        setOpenStandardDialog(true);
      }
    } catch (error) {
      console.error('Pre-discharge process failed', error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const closeDialogs = () => {
    setDischargeError(null);
    retainedBeacon.reset();
    setOpenLastPatientDialog(false);
    setOpenAnesthesiologistLastPatientDialog(false);
    setOpenStandardDialog(false);
  };

  const retentionContext = retainedBeacon.requiresChoice ? (
    <RetainedBeaconDischargeChoice eligibility={retainedBeacon.eligibility} />
  ) : null;
  const showRetentionChoiceActions = retainedBeacon.requiresChoice && retainedBeacon.choice === null;
  const retentionChoiceActions = (
    <RetainedBeaconDischargeActions
      onChoose={choice => retainedBeacon.setChoice(choice)}
      onCancel={closeDialogs}
      working={isProcessing}
    />
  );

  return (
    <>
      <LinkButton onClick={initiateDischargeProcess} disabled={isProcessing}>
        {isProcessing ? 'Processing...' : 'Discharge'}
      </LinkButton>

      {/* Last Patient Discharge Dialog */}
      <Dialog open={openLastPatientDialog} onClose={closeDialogs} aria-labelledby="last-patient-discharge-dialog-title">
        <DialogTitle id="last-patient-discharge-dialog-title">{RETAINED_BEACON_DISCHARGE_COPY.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>Send Physician daily recap message.</DialogContentText>
          <DialogContentText>
            Message will be sent to {patient?.physician?.name} at {patient?.physician?.notificationNumbers}.
          </DialogContentText>
          <DialogContentText>
            Hi [Physician's Name], your last patient [Patient's Full Name] was discharged at [Current Time]. Here are
            your analytics for the day: Average PIPO (Patient in Patient out): [Average PIPO], Average Surgery Time:
            [Average Surgery Time], Average Closing Time: [Average Closing Time], Average Turnover: [Average Turnover].
          </DialogContentText>
          {retentionContext}
          {dischargeError && <DialogContentText color="error">{dischargeError}</DialogContentText>}
        </DialogContent>
        <DialogActions style={{ flexWrap: 'wrap', gap: 8 }}>
          {showRetentionChoiceActions ? (
            retentionChoiceActions
          ) : (
            <>
              <Button onClick={() => executeDischarge(true)} color="primary" disabled={isProcessing}>
                Discharge and Send
              </Button>
              <Button onClick={() => executeDischarge(false)} color="primary" disabled={isProcessing}>
                Discharge
              </Button>
              <Button onClick={closeDialogs} color="secondary" disabled={isProcessing}>
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Anesthesiologist Last Patient Discharge Dialog */}
      <Dialog
        open={openAnesthesiologistLastPatientDialog}
        onClose={closeDialogs}
        aria-labelledby="anesthesiologist-last-patient-discharge-dialog-title"
      >
        <DialogTitle id="anesthesiologist-last-patient-discharge-dialog-title">
          {RETAINED_BEACON_DISCHARGE_COPY.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>Send Anesthesiologist daily recap message.</DialogContentText>
          <DialogContentText>
            Message will be sent to {patient?.anesthesiologist?.name} at {patient?.anesthesiologist?.phoneNumber}.
          </DialogContentText>
          <DialogContentText>
            Hi [Anesthesiologist's Name], your last patient [Patient's Full Name] was discharged at [Current Time]. Here
            are your analytics for the day: Average Patient In to Anesthesia Start, Average Anesthesia Start to
            Complete.
          </DialogContentText>
          {retentionContext}
          {dischargeError && <DialogContentText color="error">{dischargeError}</DialogContentText>}
        </DialogContent>
        <DialogActions style={{ flexWrap: 'wrap', gap: 8 }}>
          {showRetentionChoiceActions ? (
            retentionChoiceActions
          ) : (
            <>
              <Button onClick={() => executeDischarge(false, true)} color="primary" disabled={isProcessing}>
                Discharge and Send
              </Button>
              <Button onClick={() => executeDischarge(false, false)} color="primary" disabled={isProcessing}>
                Discharge
              </Button>
              <Button onClick={closeDialogs} color="secondary" disabled={isProcessing}>
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Standard Discharge Dialog */}
      <Dialog open={openStandardDialog} onClose={closeDialogs} aria-labelledby="standard-discharge-dialog-title">
        <DialogTitle id="standard-discharge-dialog-title">{RETAINED_BEACON_DISCHARGE_COPY.title}</DialogTitle>
        <DialogContent>
          {!retainedBeacon.requiresChoice && (
            <DialogContentText>Are you sure you want to discharge this patient?</DialogContentText>
          )}
          {retentionContext}
          {dischargeError && <DialogContentText color="error">{dischargeError}</DialogContentText>}
        </DialogContent>
        <DialogActions style={{ flexWrap: 'wrap', gap: 8 }}>
          {retainedBeacon.requiresChoice ? (
            <RetainedBeaconDischargeActions
              onChoose={choice => executeDischarge(false, false, choice)}
              onCancel={closeDialogs}
              working={isProcessing}
            />
          ) : (
            <>
              <Button onClick={closeDialogs} color="primary" disabled={isProcessing}>
                {RETAINED_BEACON_DISCHARGE_COPY.cancel}
              </Button>
              <Button onClick={() => executeDischarge(false, false)} color="primary" disabled={isProcessing}>
                Discharge Patient
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

const DischargeActionLiason = compose(
  graphql(discharge, {
    options: ({ id }) => ({
      refetchQueries: [
        {
          query: item,
          variables: { id: parseInt(id, 10) },
        },
      ],
    }),
  }),
  graphql(preDischarge),
  graphql(dischargePhysicianLastPatient),
  graphql(dischargeAnesthesiologistLastPatient)
)(BaseDischargeLinkButton);

export default withScope(DischargeActionLiason);
